import type { Ref } from 'react';

import { cn } from '@repo/lib/cn';
import { format } from 'date-fns';
import { ArrowRightIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  combineDateTime,
  DateTimePicker,
  DateTimePickerCalendar,
  DateTimePickerClearButton,
  DateTimePickerModeSelector,
  DateTimePickerTimeInput,
  parseISOToDateTimeValue,
} from '@/components/ui/date-time-picker';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Drawer, DrawerBody, DrawerContent, DrawerDescription, DrawerFooter, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { useMediaQuery } from '@/hooks/media-query';

import type { DateTimePickerMode, DateTimeRangeValue } from './DateTimePicker.type';

export interface DateTimeRangePickerProps {
  name?: string;
  disabled?: boolean;
  /** Start date/time as ISO string */
  from?: string | null;
  /** End date/time as ISO string */
  to?: string | null;
  /** Callback when start changes */
  onFromChange: (value: string | null) => void;
  /** Callback when end changes */
  onToChange: (value: string | null) => void;
  /** Class name for trigger */
  className?: string;
  /** Callback when input loses focus */
  onBlur?: () => void;
  /** Callback when input gains focus */
  onFocus?: () => void;
  /** Ref for trigger */
  ref?: Ref<HTMLButtonElement>;
}

export function DateTimeRangePicker(props: DateTimeRangePickerProps) {
  const { from, to, onFromChange, onToChange, className, onBlur, ref, name, disabled } = props;

  const [open, setOpen] = useState<boolean>(false);
  const breakpoint = useMediaQuery('sm');

  const rangeValue = useMemo<DateTimeRangeValue>(
    () => ({
      from: parseISOToDateTimeValue(from),
      to: parseISOToDateTimeValue(to),
    }),
    [from, to]
  );

  const defaultMode = useMemo<DateTimePickerMode>(() => {
    const hasFrom = Boolean(rangeValue?.from?.date);
    const hasTo = Boolean(rangeValue?.to?.date);
    if (hasFrom && !hasTo) return 'start';
    if (!hasFrom && hasTo) return 'end';
    return 'range';
  }, [rangeValue]);

  const handleRangeChange = (newValue: DateTimeRangeValue) => {
    if (newValue.from?.date) {
      onFromChange(combineDateTime(newValue.from.date, newValue.from.time));
    } else {
      onFromChange(null);
    }
    if (newValue.to?.date) {
      onToChange(combineDateTime(newValue.to.date, newValue.to.time));
    } else {
      onToChange(null);
    }
  };

  const hasFrom = Boolean(rangeValue?.from?.date);
  const hasTo = Boolean(rangeValue?.to?.date);

  const trigger = (
    <Button
      ref={ref}
      type={'button'}
      variant={'outline'}
      name={name}
      disabled={disabled}
      onBlur={onBlur}
      className={cn('group relative flex h-24 min-h-24 w-full items-stretch overflow-hidden rounded-2xl p-0', className)}
    >
      <div className="flex h-full w-full items-stretch">
        <div
          data-state={hasFrom ? 'filled' : 'empty'}
          className={cn(
            'flex flex-1 flex-col items-start justify-center px-4 transition-colors',
            'data-[state=filled]:bg-surface-primary data-[state=empty]:bg-surface-secondary/50',
            'group-hover:data-[state=filled]:bg-surface-secondary group-hover:data-[state=empty]:bg-surface-secondary'
          )}
        >
          <p className="text-text-tertiary text-[10px] font-bold tracking-wider uppercase">Od</p>
          {hasFrom ? (
            <>
              <p className="text-sm leading-tight font-semibold">{format(rangeValue.from!.date!, 'LLL dd, y')}</p>
              <p className="text-text-tertiary text-xs leading-tight">{rangeValue.from!.time}</p>
            </>
          ) : (
            <p className="text-text-tertiary text-sm font-medium">Nie wybrano</p>
          )}
        </div>
        <div
          data-state={hasFrom && hasTo ? 'filled' : 'empty'}
          className={cn(
            'flex items-center justify-center px-2 transition-colors',
            'data-[state=filled]:bg-surface-primary data-[state=empty]:bg-surface-secondary/50',
            'group-hover:data-[state=filled]:bg-surface-secondary group-hover:data-[state=empty]:bg-surface-secondary'
          )}
        >
          <ArrowRightIcon className={cn('size-4', hasFrom && hasTo ? 'text-text-primary' : 'text-text-tertiary')} />
        </div>
        <div
          data-state={hasTo ? 'filled' : 'empty'}
          className={cn(
            'flex flex-1 flex-col items-start justify-center px-4 transition-colors',
            'data-[state=filled]:bg-surface-primary data-[state=empty]:bg-surface-secondary/50',
            'group-hover:data-[state=filled]:bg-surface-secondary group-hover:data-[state=empty]:bg-surface-secondary'
          )}
        >
          <p className="text-text-tertiary text-[10px] font-bold tracking-wider uppercase">Do</p>
          {hasTo ? (
            <>
              <p className="text-sm leading-tight font-semibold">{format(rangeValue.to!.date!, 'LLL dd, y')}</p>
              <p className="text-text-tertiary text-xs leading-tight">{rangeValue.to!.time}</p>
            </>
          ) : (
            <p className="text-text-tertiary text-sm font-medium">Nie wybrano</p>
          )}
        </div>
      </div>
    </Button>
  );

  const content = (
    <div className="flex flex-col overflow-hidden">
      <div className="bg-surface-secondary/30 border-b p-3">
        <DateTimePickerModeSelector modes={['start', 'end', 'range']} />
      </div>
      <div className="flex flex-col lg:flex-row">
        <div className="flex-1 border-b lg:border-r lg:border-b-0">
          <DateTimePickerCalendar numberOfMonths={breakpoint ? 2 : 1} className="p-4" />
        </div>
        <div className="lg:bg-surface-secondary/10 flex w-full flex-col gap-4 p-4 lg:w-72">
          <div className="flex flex-col gap-3">
            <p className="text-text-tertiary px-1 text-[10px] font-semibold tracking-wider uppercase">Ustawienia czasu</p>
            <div className="flex flex-col gap-2">
              <DateTimePickerTimeInput target="from" />
              <DateTimePickerTimeInput target="to" />
            </div>
          </div>

          <div className="bg-fill-brand-secondary/20 text-text-secondary mt-auto rounded-2xl p-3 text-xs">
            <p className="flex items-start gap-2">
              <span className="bg-fill-brand mt-0.5 block size-1.5 shrink-0 rounded-full" />
              Wybierz zakres dat na kalendarzu, a następnie dostosuj godziny dla każdego punktu.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const footer = (
    <div className="flex w-full items-center justify-between gap-2">
      <DateTimePickerClearButton />
      <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(false)}>
        Zastosuj
      </Button>
    </div>
  );

  if (breakpoint) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DateTimePicker defaultMode={defaultMode} rangeValue={rangeValue} onRangeChange={handleRangeChange} numberOfMonths={breakpoint ? 2 : 1}>
          <DialogContent className="w-auto max-w-md p-0" isClosable={false}>
            <DialogTitle className="sr-only">Dostęp czasowy</DialogTitle>
            <DialogDescription className="sr-only">Wybierz datę i czas dostępności</DialogDescription>
            <DialogBody className="p-0">
              <div className="overflow-auto">{content}</div>
            </DialogBody>
            <DialogFooter className="bg-accent border-t">{footer}</DialogFooter>
          </DialogContent>
        </DateTimePicker>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>{trigger}</DrawerTrigger>
      <DateTimePicker defaultMode={defaultMode} rangeValue={rangeValue} onRangeChange={handleRangeChange} numberOfMonths={breakpoint ? 2 : 1}>
        <DrawerContent>
          <DrawerTitle className="sr-only">Dostęp czasowy</DrawerTitle>
          <DrawerDescription className="sr-only">Wybierz datę i czas dostępności</DrawerDescription>
          <DrawerBody className="p-0">
            <div className="overflow-auto">{content}</div>
          </DrawerBody>
          <DrawerFooter className="justify-end border-t">{footer}</DrawerFooter>
        </DrawerContent>
      </DateTimePicker>
    </Drawer>
  );
}
